<template>
  <BaseDialog
    :open="open"
    role="dialog"
    aria-modal="true"
    max-width="1060px"
    @close="onClose"
  >
    <template #title>
      Schüler zuweisen – <strong>{{ userName }}</strong>
    </template>

    <!-- Formularzeile über den Containern -->
    <div
      class="form"
      style="display:grid; grid-template-columns: 1fr 2fr; gap:12px; margin-bottom:12px;"
    >
      <div class="field">
        <label for="relationshipId">Beziehung</label>
        <select
          id="relationshipId"
          v-model.number="form.relationshipId"
          :disabled="loading"
          required
        >
          <option
            :value="null"
            disabled
          >
            Bitte wählen…
          </option>
          <option
            v-for="rel in relationships"
            :key="rel.id"
            :value="rel.id"
          >
            {{ rel.label }}
          </option>
        </select>
      </div>
      <div class="field">
        <label for="notes">Notizen (optional)</label>
        <input
          id="notes"
          v-model="form.notes"
          type="text"
          :disabled="loading"
          placeholder="optional"
        >
      </div>
    </div>

    <div class="columns">
      <!-- Verfügbare -->
      <section class="field">
        <label for="searchAvailable">Verfügbare Schüler</label>
        <input
          id="searchAvailable"
          v-model.trim="searchAvailable"
          :disabled="loading"
          type="search"
          placeholder="Suchen (Name, Nummer)…"
          class="search"
        >
        <div
          class="chip-box"
          style="margin-top:8px;"
        >
          <div class="chips">
            <button
              v-for="s in filteredAvailable"
              :key="s.id"
              type="button"
              class="chip"
              :title="studentLabel(s)"
              @click="onAddChip(s)"
            >
              {{ studentLabel(s) }}
            </button>
            <p
              v-if="filteredAvailable.length===0"
              class="muted"
            >
              Keine Treffer.
            </p>
          </div>
        </div>
      </section>

      <!-- Zugewiesene -->
      <section class="field">
        <label>Zugewiesene Schüler</label>
        <div class="chip-box">
          <div class="chips">
            <div
              v-if="assignments.length === 0"
              class="muted"
            >
              Noch keine Zuordnungen.
            </div>
            <template v-else>
              <button
                v-for="a in assignments"
                :key="a.studentId"
                type="button"
                class="chip active"
                :title="assignedLabel(a)"
                @click="onRemoveChip(a)"
              >
                {{ assignedLabel(a) }}
              </button>
            </template>
          </div>
        </div>
      </section>
    </div>

    <template #footer>
      <div class="footer">
        <div class="left">
          <span
            v-if="error"
            class="error"
          >{{ error }}</span>
        </div>
        <div class="right">
          <button
            type="button"
            class="btn"
            :disabled="loading"
            @click="onClose"
          >
            Abbrechen
          </button>
          <button
            type="button"
            class="btn primary"
            :disabled="loading || !canSave"
            @click="onSave"
          >
            {{ loading ? 'Speichern…' : 'Speichern' }}
          </button>
        </div>
      </div>
    </template>
  </BaseDialog>
</template>

<script setup>
/* eslint-disable no-console */

import { computed, onMounted, ref, watch } from 'vue';
import BaseDialog from '@/components/BaseDialog.vue';
import api from '@/services/api';
import {useToast} from 'primevue/usetoast';

// API-Module – passe die Pfade/Methoden bei Bedarf an
import usersApi from '@/api/users';
import parentStudentsApi from '@/api/parentStudents';
import parentRelationshipsApi from '@/api/parentRelationships';

const props = defineProps({
  open: { type: Boolean, default: false },
  userId: { type: Number, required: true }
});
const emit = defineEmits(['close', 'changed']);

const loading = ref(false);
const error = ref(null);
const toast = useToast();

const userName = ref('');
const assignments = ref([]);            // aktuelle (rechte) Liste: { studentId, relationshipId, notes, student? }
const availableStudents = ref([]);      // aktuelle (linke) Liste: { id, first_name, last_name, student_number? }
const relationships = ref([]);          // { id, label }
const studentById = ref(new Map());     // Map<number, { id, first_name, last_name, student_number? }>

const initialAssignedIds = ref(new Set());     // Snapshot bei Öffnen
const initialAssignmentMap = ref(new Map());   // studentId -> { relationshipId, notes }
const assignedIds = ref(new Set());            // aktueller Zustand

const form = ref({
  studentId: null,
  relationshipId: null,
  notes: ''
});

const searchAvailable = ref('');

/** Reset nur die Formularfelder */
function resetForm() {
  form.value = { studentId: null, relationshipId: null, notes: '' };
}

/** Gesamten Dialogzustand leeren */
function clearState() {
  // Inhalte leeren
  assignments.value = [];
  availableStudents.value = [];
  relationships.value = [];
  studentById.value = new Map();

  // Snapshots/Diffs zurücksetzen
  initialAssignedIds.value = new Set();
  initialAssignmentMap.value = new Map();
  assignedIds.value = new Set();

  // Anzeige-/Form-/Fehlerzustände leeren
  userName.value = '';
  searchAvailable.value = '';
  error.value = null;
  resetForm();
}

/** Anzeigename für Schüler in der linken Liste */
function studentLabel(s) {
  const last = String(s?.last_name ?? s?.lastName ?? '').trim();
  const first = String(s?.first_name ?? s?.firstName ?? '').trim();
  const num = String(s?.student_number ?? s?.number ?? '').trim();
  const name = (last || first) ? `${last}${last && first ? ', ' : ''}${first}` : `#${s?.id}`;
  return num ? `${name} (${num})` : name;
}

/** Anzeigename für zugewiesene Elemente in der rechten Liste */
function assignedLabel(a) {
  const id = Number(a?.studentId);
  const fromMap = studentById.value.get(id);
  const s = a?.student || fromMap || {};
  const base = studentLabel({ ...s, id });
  const rel = relationships.value.find(r => Number(r.id) === Number(a.relationshipId));
  const relTxt = rel ? ` – ${rel.label}` : '';
  const notes = a?.notes ? ` | ${a.notes}` : '';
  return `${base}${relTxt}${notes}`;
}

/** Abgeleitete Liste „verfügbar“ (nicht zugewiesen + Suchfilter) */
const filteredAvailable = computed(() => {
  const q = (searchAvailable.value || '').trim().toLowerCase();
  const assigned = assignedIds.value;

  const base = Array.isArray(availableStudents.value) ? availableStudents.value : [];
  const notAssigned = base.filter(s => !assigned.has(Number(s?.id)));

  if (!q) return notAssigned;

  return notAssigned.filter(s => {
    const last = String(s?.last_name ?? s?.lastName ?? '').toLowerCase();
    const first = String(s?.first_name ?? s?.firstName ?? '').toLowerCase();
    const num = String(s?.student_number ?? s?.number ?? '').toLowerCase();
    const name = `${last} ${first}`.trim();
    return last.includes(q) || first.includes(q) || name.includes(q) || num.includes(q);
  });
});

/** Änderungen gegenüber dem Initialzustand berechnen */
const diff = computed(() => {
  const additions = [];
  const removals = [];
  const updates = [];

  const initial = initialAssignedIds.value;
  const current = assignedIds.value;

  // Additionen
  for (const id of current) {
    if (!initial.has(id)) additions.push(id);
  }
  // Removals
  for (const id of initial) {
    if (!current.has(id)) removals.push(id);
  }
  // Updates (in beiden Sets, aber Beziehung/Notes geändert)
  for (const id of current) {
    if (initial.has(id)) {
      const was = initialAssignmentMap.value.get(id) || { relationshipId: null, notes: null };
      const nowRow = assignments.value.find(a => Number(a.studentId) === Number(id));
      const now = {
        relationshipId: Number(nowRow?.relationshipId ?? nowRow?.relationship_id ?? 0) || null,
        notes: nowRow?.notes ?? null
      };
      if (was.relationshipId !== now.relationshipId || (was.notes ?? '') !== (now.notes ?? '')) {
        updates.push({ id, payload: now });
      }
    }
  }

  return { additions, removals, updates };
});

/** „Speichern“ nur aktivieren, wenn es Änderungen gibt */
const canSave = computed(() => {
  const d = diff.value;
  return (d.additions.length + d.removals.length + d.updates.length) > 0;
});

/** Loader: Benutzername */
async function loadUserName() {
  if (!props.userId) return;
  try {
    const res = await usersApi.getOne(props.userId);
    const u = res?.data?.data ?? res?.data ?? {};
    const last = u.last_name ?? u.lastName ?? '';
    const first = u.first_name ?? u.firstName ?? '';

    userName.value = (last || first) ? `${first} ${last}`.trim() : (u.name ?? `#${props.userId}`);
  } catch (e) {
    console.error('[ParentAssignDialog] loadUserName error:', e?.response || e);
    userName.value = `#${props.userId}`;
  }
}

/** Loader: Beziehungen */
async function loadRelationships() {
  try {
    // TODO: ggf. richtige Methode verwenden (all/list/index)
    const res = await (parentRelationshipsApi.list?.() ?? parentRelationshipsApi.all?.() ?? parentRelationshipsApi.index?.());
    const raw = res?.data?.data ?? res?.data ?? [];
    relationships.value = raw.map(r => ({
      id: Number(r?.id),
      // bevorzugt label_de
      label: String(r?.label_de ?? r?.labelDe ?? r?.label ?? r?.name ?? r?.title ?? '').trim() || `#${r?.id}`
    })).filter(r => Number.isFinite(r.id));
  } catch (e) {
    console.error('[ParentAssignDialog] loadRelationships error:', e?.response || e);
    relationships.value = [];
  }
}

/** Loader: Zuweisungen (rechte Liste) */
async function loadAssignments() {
  if (!props.userId) return;
  try {
    // TODO: richtige Methode der API einsetzen
    const res = await (parentStudentsApi.list?.(props.userId) ?? parentStudentsApi.index?.(props.userId));
    const raw = res?.data?.data ?? res?.data ?? [];

    const mapped = raw.map(r => {
      const student = r.student ?? null;
      const studentId = Number(
          r.studentId ?? r.student_id ?? student?.id ?? r.id
      );
      const relationshipId = Number(
          r.relationshipId ?? r.relationship_id ?? r.user_relationship_id ?? 0
      ) || null;
      const notes = r.notes ?? '';

      return { studentId, relationshipId, notes, student };
    }).filter(r => Number.isFinite(r.studentId) && r.studentId > 0);

    assignments.value = mapped;
    assignedIds.value = new Set(mapped.map(r => Number(r.studentId)));

    // Snapshots für Diff-Berechnung initial setzen
    initialAssignedIds.value = new Set(assignedIds.value);
    initialAssignmentMap.value = new Map(
        mapped.map(r => [Number(r.studentId), {
          relationshipId: Number(r.relationshipId ?? 0) || null,
          notes: r.notes ?? null
        }])
    );
  } catch (e) {
    console.error('[ParentAssignDialog] loadAssignments error:', e?.response || e);
    assignments.value = [];
    assignedIds.value = new Set();
    initialAssignedIds.value = new Set();
    initialAssignmentMap.value = new Map();
  }
}

/** Loader: Verfügbare Schüler (linke Liste) – nur aktive (status: 1) */
async function loadAvailableStudents() {
  if (!props.userId) return;
  try {
    const { data } = await api.get('/students', {
      params: { page: 1, limit: 1000, status: 1 }
    });
    const items = data?.items ?? data?.data ?? [];
    const mapped = items.map(s => ({
      id: Number(s?.id),
      first_name: s?.first_name ?? s?.firstName ?? '',
      last_name:  s?.last_name  ?? s?.lastName  ?? '',
      student_number: s?.student_number ?? s?.number ?? ''
    })).filter(s => Number.isFinite(s.id) && s.id > 0);

    // Map mit allen aktiven Schülern (auch zugewiesene) füllen
    const m = new Map(studentById.value);
    for (const s of mapped) {
      m.set(Number(s.id), s);
    }
    studentById.value = m;

    // Bereits zugewiesene herausfiltern (für linke Liste)
    const assigned = assignedIds.value;
    availableStudents.value = mapped.filter(s => !assigned.has(Number(s.id)));
  } catch (e) {
    console.error('[ParentAssignDialog] loadAvailableStudents error:', e?.response || e);
    availableStudents.value = [];
  }
}

/** Chip-Add: aus links nach rechts übernehmen */
function onAddChip(s) {
  if (!s) return;

  if (!form.value.relationshipId) {
    error.value = 'Bitte zuerst eine Beziehung auswählen.';
    return;
  }

  const id = Number(s.id);
  if (assignedIds.value.has(id)) return;

  // Rechts ergänzen (Set reaktiv aktualisieren)
  const nextAssigned = new Set(assignedIds.value);
  nextAssigned.add(id);
  assignedIds.value = nextAssigned;

  assignments.value.push({
    studentId: id,
    relationshipId: form.value.relationshipId,
    notes: form.value.notes ?? '',
    student: s
  });

  // Links entfernen
  availableStudents.value = (availableStudents.value || []).filter(st => Number(st.id) !== id);

  // optional: Formular zurücksetzen, damit nächste Zuweisung bewusst getroffen wird
  resetForm();
  error.value = null;
}

/** Chip-Remove: aus rechts entfernen und nach links zurückschieben */
function onRemoveChip(a) {
  const id = Number(a?.studentId);
  if (!assignedIds.value.has(id)) return;

  // Rechts entfernen (Set reaktiv aktualisieren)
  const nextAssigned = new Set(assignedIds.value);
  nextAssigned.delete(id);
  assignedIds.value = nextAssigned;

  assignments.value = (assignments.value || []).filter(x => Number(x.studentId) !== id);

  // Links hinzufügen (wenn noch nicht vorhanden)
  const alreadyInPool = (availableStudents.value || []).some(st => Number(st.id) === id);
  if (!alreadyInPool) {
    // Bestes verfügbares Studentenobjekt ermitteln
    const m = studentById.value;
    const known =
      a?.student ||
      (m && typeof m.get === 'function' ? m.get(id) : null) ||
      { id, first_name: '', last_name: '', student_number: '' };

    // Vorn einfügen
    availableStudents.value = [known, ...(availableStudents.value || [])];
  }
}

/** Speichern: Add/Remove/Update gemäß diff */
async function onSave() {
  loading.value = true;
  error.value = null;
  try {
    const { additions, removals, updates } = diff.value;

    // Removals
    for (const id of removals) {
      // TODO: richtige Delete-Methode eintragen
      if (typeof parentStudentsApi.remove === 'function') {
        await parentStudentsApi.remove(props.userId, id);
      } else if (typeof parentStudentsApi.delete === 'function') {
        await parentStudentsApi.delete(props.userId, id);
      } else {
        console.warn('Kein remove/delete verfügbar – bitte API anpassen.');
      }
    }

    // Additions
    for (const id of additions) {
      const row = assignments.value.find(x => Number(x.studentId) === Number(id));
      const payload = {
        studentId: id,
        relationshipId: Number(row?.relationshipId ?? 0) || null,
        notes: row?.notes ?? ''
      };
      // TODO: richtige Create-Methode eintragen
      if (typeof parentStudentsApi.add === 'function') {
        await parentStudentsApi.add(props.userId, payload);
      } else if (typeof parentStudentsApi.create === 'function') {
        await parentStudentsApi.create(props.userId, payload);
      } else if (typeof parentStudentsApi.post === 'function') {
        await parentStudentsApi.post(props.userId, payload);
      } else {
        console.warn('Keine add/create/post verfügbar – bitte API anpassen.');
      }
    }

    // Updates
    for (const u of updates) {
      const payload = {
        relationshipId: Number(u.payload?.relationshipId ?? 0) || null,
        notes: u.payload?.notes ?? ''
      };
      // TODO: richtige Update-Methode eintragen
      if (typeof parentStudentsApi.update === 'function') {
        await parentStudentsApi.update(props.userId, u.id, payload);
      } else if (typeof parentStudentsApi.patch === 'function') {
        await parentStudentsApi.patch(props.userId, u.id, payload);
      } else if (typeof parentStudentsApi.put === 'function') {
        await parentStudentsApi.put(props.userId, u.id, payload);
      } else {
        console.warn('Keine update/patch/put verfügbar – bitte API anpassen.');
      }
    }

    // Nach erfolgreichem Speichern frisch laden, um den neuen Initialzustand zu setzen
    await loadAssignments();
    await loadAvailableStudents();

    toast.add({severity: 'success', summary: 'Gespeichert.', life: 3500});
    emit('changed');   // parent kann zusätzlich reagieren
    onClose();         // Dialog schließen (falls gewünscht)

  } catch (e) {
    console.error('[ParentAssignDialog] onSave error:', e?.response || e);
    error.value = 'Speichern fehlgeschlagen. Bitte erneut versuchen.';
  } finally {
    loading.value = false;
  }
}

function onClose() {
  emit('close');
}

/** Initial-Ladevorgang und Re-Load beim Öffnen */
onMounted(async () => {
  if (!props.open) return;
  clearState();
  loading.value = true;
  try {
    await Promise.all([loadUserName(), loadRelationships()]);
    await loadAssignments();
    await loadAvailableStudents();
    resetForm();
  } finally {
    loading.value = false;
  }
});

watch(() => props.open, async (now) => {
  if (!now) {
    clearState(); // beim Schließen aufräumen
    return;
  }
  clearState();   // vor dem Laden leeren
  loading.value = true;
  try {
    await Promise.all([loadUserName(), loadRelationships()]);
    await loadAssignments();
    await loadAvailableStudents();
    resetForm();
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.field {
  display: flex;
  flex-direction: column;
}

label {
  font-weight: 600;
  margin-bottom: 4px;
}

.search {
  width: 100%;
}

.footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.footer .right {
  display: flex;
  gap: 8px;
}

.btn {
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
}

.btn.primary {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
}

.error {
  color: #dc2626;
}

.muted {
  color: #6b7280;
  font-size: 13px;
}

.chip-box {
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 8px;
  min-height: 48px;
  max-height: 260px;
  overflow-y: auto;
  background: var(--surface);
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 13px;
  line-height: 1.4;
  cursor: pointer;
}
/* keine Deaktivierungen an Chips */
.chip.active {
  background: rgba(79,70,229,0.12);
  border-color: var(--primary);
  color: var(--primary-600);
}
</style>
